import { getGooglePlacesApiKey, isGooglePlacesEnabled } from "./config";
import type { PlacesAutocompleteOptions } from "./autocompleteOptions";
import {
  fetchEdgePlaceDetails,
  fetchEdgePlacesAutocomplete,
  type PlacesServiceError,
} from "./edgeProxy";
import { toPlacesApiLocationBias } from "./locationBias";
import { mapPlaceDetailsFromApi, resolveStreetLine } from "./normalize";
import {
  mergePlaceSuggestions,
  rankBusinessSuggestions,
  stripLegalBusinessSuffixes,
} from "./rankBusinessSuggestions";
import type {
  GooglePlaceDetails,
  GooglePlacesAutocompleteMode,
  GooglePlaceSuggestion,
} from "./types";

export class GooglePlacesUnavailableError extends Error {
  readonly code: PlacesServiceError["code"];

  constructor(message: string, code: PlacesServiceError["code"] = "unknown") {
    super(message);
    this.name = "GooglePlacesUnavailableError";
    this.code = code;
  }
}

const normalizePlaceIdForNewApi = (placeId: string) =>
  placeId.trim().replace(/^places\//, "");

const looksLikePostalCode = (input: string) =>
  /^\d{3,5}(-\d{0,4})?$/.test(input.trim());

const withOptionalCity = (query: string, nearbyCity?: string | null) => {
  const city = String(nearbyCity ?? "").trim();
  if (!city) return query;
  if (query.toLowerCase().includes(city.toLowerCase())) return query;
  return `${query} ${city}`;
};

const fetchNewPlacesAutocomplete = async (
  input: string,
  mode: GooglePlacesAutocompleteMode,
  signal?: AbortSignal,
  primaryTypes?: string[],
  locationBias?: PlacesAutocompleteOptions["locationBias"],
): Promise<
  | { ok: true; data: GooglePlaceSuggestion[] }
  | { ok: false; status: number; detail?: string }
> => {
  const body: Record<string, unknown> = {
    input: input.trim(),
    languageCode: "en",
    regionCode: "US",
  };

  // US hard-filter for both modes (business previously lacked this).
  body.includedRegionCodes = ["us"];

  if (mode === "business") {
    body.includedPrimaryTypes = ["establishment"];
  } else if (primaryTypes?.length) {
    body.includedPrimaryTypes = primaryTypes;
  }

  if (locationBias) {
    body.locationBias = toPlacesApiLocationBias(locationBias);
  }

  const response = await fetch(
    "https://places.googleapis.com/v1/places:autocomplete",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": getGooglePlacesApiKey(),
        "X-Goog-FieldMask":
          "suggestions.placePrediction.placeId,suggestions.placePrediction.text.text",
      },
      body: JSON.stringify(body),
      signal,
    },
  );

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    if (import.meta.env.DEV) {
      console.warn(
        "[Google Places] autocomplete (New API) failed:",
        response.status,
        detail,
      );
    }
    return { ok: false, status: response.status, detail };
  }

  const payload = (await response.json()) as {
    suggestions?: Array<{
      placePrediction?: { placeId?: string; text?: { text?: string } };
    }>;
  };

  const data =
    payload.suggestions
      ?.map((item) => ({
        placeId: normalizePlaceIdForNewApi(
          String(item.placePrediction?.placeId ?? ""),
        ),
        text: String(item.placePrediction?.text?.text ?? ""),
      }))
      .filter((item) => item.placeId && item.text)
      .slice(0, 8) ?? [];

  return { ok: true, data };
};

/** Places API (New) Text Search — finds low-rank / service-area businesses better. */
const fetchNewPlacesTextSearch = async (
  textQuery: string,
  signal?: AbortSignal,
  locationBias?: PlacesAutocompleteOptions["locationBias"],
): Promise<GooglePlaceSuggestion[]> => {
  const body: Record<string, unknown> = {
    textQuery: textQuery.trim(),
    languageCode: "en",
    regionCode: "US",
  };
  if (locationBias) {
    body.locationBias = toPlacesApiLocationBias(locationBias);
  }

  const response = await fetch(
    "https://places.googleapis.com/v1/places:searchText",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": getGooglePlacesApiKey(),
        "X-Goog-FieldMask":
          "places.id,places.displayName,places.formattedAddress",
      },
      body: JSON.stringify(body),
      signal,
    },
  );

  if (!response.ok) {
    if (import.meta.env.DEV) {
      const detail = await response.text().catch(() => "");
      console.warn(
        "[Google Places] searchText failed:",
        response.status,
        detail,
      );
    }
    return [];
  }

  const payload = (await response.json()) as {
    places?: Array<{
      id?: string;
      displayName?: { text?: string };
      formattedAddress?: string;
    }>;
  };

  return (
    payload.places
      ?.map((place) => {
        const placeId = normalizePlaceIdForNewApi(String(place.id ?? ""));
        const name = String(place.displayName?.text ?? "").trim();
        const address = String(place.formattedAddress ?? "").trim();
        const text = [name, address].filter(Boolean).join(", ");
        return { placeId, text };
      })
      .filter((item) => item.placeId && item.text)
      .slice(0, 8) ?? []
  );
};

const fetchBusinessSuggestionsDirect = async (
  input: string,
  options?: PlacesAutocompleteOptions,
): Promise<
  | { ok: true; data: GooglePlaceSuggestion[] }
  | { ok: false; status: number }
> => {
  const raw = input.trim();
  const stripped = stripLegalBusinessSuffixes(raw);
  const bias = options?.locationBias;
  const nearby =
    options?.nearbyCity?.trim() ||
    options?.biasCity?.trim() ||
    null;
  const signal = options?.signal;

  const strippedWithCity = withOptionalCity(
    stripped.length >= 3 ? stripped : raw,
    nearby,
  );
  const textQuery = withOptionalCity(raw, nearby);

  const [rawResult, strippedResult, textSearch] = await Promise.all([
    fetchNewPlacesAutocomplete(raw, "business", signal, undefined, bias),
    stripped !== raw && stripped.length >= 3
      ? fetchNewPlacesAutocomplete(
          strippedWithCity,
          "business",
          signal,
          undefined,
          bias,
        )
      : Promise.resolve({ ok: true as const, data: [] as GooglePlaceSuggestion[] }),
    fetchNewPlacesTextSearch(textQuery, signal, bias),
  ]);

  if (!rawResult.ok && !strippedResult.ok) {
    return { ok: false, status: rawResult.status };
  }

  const merged = mergePlaceSuggestions(
    rawResult.ok ? rawResult.data : [],
    strippedResult.ok ? strippedResult.data : [],
    textSearch,
  );
  if (merged.length === 0 && !rawResult.ok) {
    return { ok: false, status: rawResult.status };
  }
  return {
    ok: true,
    data: rankBusinessSuggestions(raw, merged).slice(0, 8),
  };
};

export const fetchPlacesAutocomplete = async (
  input: string,
  mode: GooglePlacesAutocompleteMode,
  options?: PlacesAutocompleteOptions | AbortSignal,
): Promise<GooglePlaceSuggestion[]> => {
  // Back-compat: third arg used to be AbortSignal only.
  const opts: PlacesAutocompleteOptions =
    options instanceof AbortSignal || options === undefined
      ? { signal: options as AbortSignal | undefined }
      : options;

  if (!isGooglePlacesEnabled() || input.trim().length < 3) {
    return [];
  }

  // Prefer the Supabase proxy: avoids browser referrer restrictions and CORS.
  const proxyResult = await fetchEdgePlacesAutocomplete(input, mode, opts);
  if (proxyResult.suggestions.length > 0) {
    return mode === "business"
      ? rankBusinessSuggestions(input, proxyResult.suggestions)
      : proxyResult.suggestions;
  }

  if (mode === "business") {
    const businessResult = await fetchBusinessSuggestionsDirect(input, opts);
    if (businessResult.ok) {
      return businessResult.data;
    }
    if (proxyResult.error) {
      throw new GooglePlacesUnavailableError(
        proxyResult.error.message,
        proxyResult.error.code,
      );
    }
    if (businessResult.status === 403) {
      throw new GooglePlacesUnavailableError(
        "Google Places is unavailable. Enable billing, Places API (New), and Places API in Google Cloud, then set GOOGLE_PLACES_API_KEY on Supabase and VITE_GOOGLE_PLACES_API_KEY in the app.",
        "permission_denied",
      );
    }
    return [];
  }

  const newResult = await fetchNewPlacesAutocomplete(
    input,
    mode,
    opts.signal,
    undefined,
    opts.locationBias,
  );
  if (newResult.ok) {
    if (looksLikePostalCode(input)) {
      const zipResult = await fetchNewPlacesAutocomplete(
        input,
        mode,
        opts.signal,
        ["postal_code"],
        opts.locationBias,
      );
      if (zipResult.ok) {
        return mergePlaceSuggestions(zipResult.data, newResult.data).slice(
          0,
          8,
        );
      }
    }
    return newResult.data;
  }

  if (proxyResult.error) {
    throw new GooglePlacesUnavailableError(
      proxyResult.error.message,
      proxyResult.error.code,
    );
  }

  if (newResult.status === 403) {
    throw new GooglePlacesUnavailableError(
      "Google Places is unavailable. Enable billing, Places API (New), and Places API in Google Cloud, then set GOOGLE_PLACES_API_KEY on Supabase and VITE_GOOGLE_PLACES_API_KEY in the app.",
      "permission_denied",
    );
  }

  return [];
};

/** Prefer full formatted addresses (with ZIP) for suggestion labels. */
export const enrichPlaceSuggestionsWithAddresses = async (
  suggestions: GooglePlaceSuggestion[],
  signal?: AbortSignal,
): Promise<GooglePlaceSuggestion[]> => {
  if (suggestions.length === 0) return suggestions;

  const enriched = await Promise.all(
    suggestions.slice(0, 6).map(async (item) => {
      if (signal?.aborted) return item;
      if (/\b\d{5}(-\d{4})?\b/.test(item.text)) return item;
      try {
        const details = await fetchGooglePlaceDetails(item.placeId, signal);
        const formatted = details?.formattedAddress?.trim();
        if (formatted) {
          return { placeId: item.placeId, text: formatted };
        }
      } catch {
        // keep original prediction text
      }
      return item;
    }),
  );

  return enriched;
};

const withStreetLine = (details: GooglePlaceDetails): GooglePlaceDetails => ({
  ...details,
  streetLine:
    details.streetLine?.trim() ||
    resolveStreetLine({
      streetLine: "",
      formattedAddress: details.formattedAddress,
    }),
});

const fetchNewPlaceDetails = async (
  placeId: string,
  signal?: AbortSignal,
): Promise<GooglePlaceDetails | null | "forbidden"> => {
  const normalizedId = normalizePlaceIdForNewApi(placeId);
  const response = await fetch(
    `https://places.googleapis.com/v1/places/${encodeURIComponent(normalizedId)}`,
    {
      headers: {
        "X-Goog-Api-Key": getGooglePlacesApiKey(),
        "X-Goog-FieldMask":
          "id,displayName,formattedAddress,nationalPhoneNumber,internationalPhoneNumber,websiteUri,googleMapsUri,addressComponents,photos,rating,userRatingCount",
      },
      signal,
    },
  );

  if (response.status === 403) {
    return "forbidden";
  }

  if (!response.ok) {
    if (import.meta.env.DEV) {
      const detail = await response.text().catch(() => "");
      console.warn(
        "[Google Places] details (New API) failed:",
        response.status,
        detail,
      );
    }
    return null;
  }

  const payload = (await response.json()) as Record<string, unknown>;
  return mapPlaceDetailsFromApi(normalizedId, payload);
};

export const fetchGooglePlaceDetails = async (
  placeId: string,
  signal?: AbortSignal,
): Promise<GooglePlaceDetails | null> => {
  if (!isGooglePlacesEnabled() || !placeId.trim()) {
    return null;
  }

  const proxyResult = await fetchEdgePlaceDetails(placeId, signal);
  if (proxyResult.details) {
    return withStreetLine(proxyResult.details);
  }

  const result = await fetchNewPlaceDetails(placeId, signal);
  if (result === "forbidden") {
    return null;
  }
  if (result) {
    return withStreetLine(result);
  }

  return proxyResult.details;
};
